import React, { FC, useState } from "react";
import { IconButton, Typography } from "@mui/material";
import FeatherIcon from "feather-icons-react";
import { FileMetaDataDto } from "@green0meter/api-client";
import { useTranslation } from "react-i18next";

import { ConfirmationModal } from "../Modals";

import { Card, Stack } from "@components/base";
import { useDeleteFile, useDownloadFile } from "@api";
import { useUserStore } from "@store/user";

interface FileListItem {
  file: FileMetaDataDto;
  onDelete?: () => Promise<void>;
}

const FileListItem: FC<FileListItem> = ({ file, onDelete }) => {
  const { user } = useUserStore();
  const { downloadFile, downloadFileEnabled } = useDownloadFile(user);
  const { deleteFileAsync, deleteFileEnabled } = useDeleteFile(user);

  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleDownload = async (): Promise<void> => {
    if (downloadFileEnabled) {
      await downloadFile({ path: file.pathName, name: file.name });
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteFileEnabled) return;
    await deleteFileAsync(file.pathName);
    await onDelete?.();
    setIsModalOpen(false);
  };

  return (
    <>
      <Card sx={{ width: "fit-content" }}>
        <Stack direction="row" alignItems="center" spacing={1} px={1} py={0.5}>
          <FeatherIcon icon="file" size={16} />
          <Typography variant="body2">{file.name}</Typography>
          {file.createdBy && (
            <Typography color="gray" fontSize="inherit">
              {file.createdBy.firstName} {file.createdBy.lastName}
            </Typography>
          )}
          {downloadFileEnabled && (
            <IconButton onClick={handleDownload} size="small">
              <FeatherIcon icon="download" size={16} />
            </IconButton>
          )}
          {deleteFileEnabled && (
            <IconButton onClick={() => setIsModalOpen(true)} size="small">
              <FeatherIcon icon="trash-2" size={16} />
            </IconButton>
          )}
        </Stack>
      </Card>
      <ConfirmationModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        onConfirm={() => {
          handleDelete();
        }}
        title={t("common.confirmDelete")}
        description={t("common.confirmDeleteDescription")}
      />
    </>
  );
};

export default FileListItem;
